/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { loadRunnerState, saveRunnerState } from "../serialization.js";
import { Registry } from "./registry.js";
import { VisitTracker } from "./visit-tracker.js";
// TODO: Support stream serialization somehow.
// see https://github.com/breadboard-ai/breadboard/issues/423
function toReanimationState(root, visits) {
    function pathToString(path) {
        return path.join("-");
    }
    function descend(entry, path, result) {
        for (const [index, child] of entry.children.entries()) {
            if (!child)
                continue;
            const newPath = [...path, index];
            if (child.data) {
                result[pathToString(path)] = child.data;
            }
            descend(child, newPath, result);
        }
    }
    const states = {};
    descend(root, [], states);
    return { states, visits: visits.visited() };
}
export class LifecycleManager {
    #stack;
    #registry;
    #visits;
    constructor(visits) {
        this.#stack = [];
        this.#registry = new Registry();
        this.#visits = new VisitTracker(visits);
    }
    async supplyPartialOutputs(outputs, invocationPath) {
        const state = this.#registry.find(invocationPath)?.data;
        if (!state) {
            console.warn(`No state found for path ${invocationPath}, partialOutputs will be dropped`);
            return;
        }
        const unpackedState = loadRunnerState(state.state).state;
        unpackedState.partialOutputs = outputs;
        state.state = saveRunnerState("nodestart", unpackedState);
    }
    dispatchGraphStart(url, path) {
        const entry = this.#registry.create(path);
        if (entry) {
            entry.data = { ...entry.data, url, path };
        }
        this.#stack.push({ url, path });
    }
    dispatchSkip() {
        // Do nothing for now.
    }
    dispatchEdge(_edge) {
        // Do nothing for now.
    }
    async dispatchNodeStart(result, invocationPath) {
        this.#visits.visit(result.descriptor.id, invocationPath);
        if (this.#stack.length === 0) {
            return;
        }
        const entry = this.#registry.create(invocationPath);
        const state = saveRunnerState("nodestart", result);
        const last = this.#stack[this.#stack.length - 1];
        if (last) {
            last.state = state;
            last.path = invocationPath;
        }
        entry.data = { url: undefined, state, path: invocationPath };
    }
    dispatchNodeEnd(outputs, invocationPath) {
        const entry = this.#registry.find(invocationPath);
        if (entry?.data) {
            entry.data.outputs = outputs;
        }
    }
    dispatchGraphEnd() {
        // TODO: implement
    }
    pathFor(node) {
        return this.#visits.pathFor(node);
    }
    state() {
        return this.#stack;
    }
    reanimationState() {
        return toReanimationState(this.#registry.root, this.#visits);
    }
}
export const traversalResultFromStack = (stack) => {
    const { state } = stack[stack.length - 1];
    return state ? loadRunnerState(state).state : undefined;
};
//# sourceMappingURL=lifecycle.js.map